import { useState } from 'react';
import { toast } from 'react-toastify';
import type { DeveloperView } from '@dunea/shared';
import { apiCreateDevToken, apiRevokeDevToken } from '~/lib/api/marketplace';
import { classNames } from '~/utils/classNames';
import { BTN_SECONDARY, CARD, ErrorBanner, Section, Spinner } from '~/components/workbench/tools/payments/ui';
import { formatDate } from '~/components/workbench/tools/marketing/marketing-model';
import { apiErrorMessage } from '~/components/marketplace/developer-model';
import { ConfirmButton, SecretReveal } from './shared';

/** CLI tokens (`DUNEA_DEV_TOKEN`): create (shown once) and revoke. */
export function TokensSection({ dev, onChanged }: { dev: DeveloperView; onChanged: () => void }) {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const create = async () => {
    setBusy(true);
    setError(null);

    try {
      const { token } = await apiCreateDevToken(label.trim() || 'CLI');
      setCreated(token);
      setLabel('');
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create a token.'));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    setRevoking(id);

    try {
      await apiRevokeDevToken(id);
      toast.success('Token revoked.');
      onChanged();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not revoke that token.'));
    } finally {
      setRevoking(null);
    }
  };

  return (
    <Section title="Developer tokens" icon="i-ph:key">
      <div className={classNames(CARD, 'space-y-3 p-4')}>
        {created && (
          <SecretReveal
            label="DUNEA_DEV_TOKEN"
            value={created}
            note="Use it as DUNEA_DEV_TOKEN for the CLI."
            onDone={() => setCreated(null)}
          />
        )}

        {dev.tokens.length > 0 ? (
          <ul className="divide-y divide-hairline">
            {dev.tokens.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 first:pt-0">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12.5px] text-primary">{t.label}</div>
                  <div className="text-[11px] text-muted">
                    <code className="font-geist-mono">{t.prefix}…</code> · created {formatDate(t.createdAt)}
                    {t.lastUsedAt ? ` · last used ${formatDate(t.lastUsedAt)}` : ' · never used'}
                  </div>
                </div>
                <ConfirmButton
                  label="Revoke"
                  icon="i-ph:trash"
                  question="Revoke it? The CLI using it stops working."
                  confirmLabel="Revoke"
                  danger
                  busy={revoking === t.id}
                  onConfirm={() => void revoke(t.id)}
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[12px] text-muted">No tokens yet. Create one to publish from the CLI.</p>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
          className="flex gap-2"
        >
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label, e.g. Laptop CLI"
            aria-label="Token label"
            maxLength={60}
            className="ui-input !py-2"
          />
          <button type="submit" disabled={busy} className={classNames(BTN_SECONDARY, 'shrink-0')}>
            {busy && <Spinner />}
            Create token
          </button>
        </form>
        {error && <ErrorBanner message={error} />}
      </div>
    </Section>
  );
}
